import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  CAMINHOS,
  ROTAS,
  type AlvoDoToolkit,
  type CriarAlvo,
  type CriarRotina,
  type CriarVerbete,
  type DecisaoToolkit,
  type Marcacao,
  type NovaExtensao,
  type NovaVersao,
  type NovaVersaoVerbete,
  type PropostaPendente,
  type Projeto,
  type RotinaDetalhe,
  type RotinaResumo,
  type VerbeteResumo,
} from '@remoto/shared';
import { chamar } from '../api';

/**
 * As consultas e os gestos da tela do Toolkit (F3.7), sobre as rotas de
 * `apps/api/src/toolkit/toolkit.controller.ts`.
 *
 * Tudo debaixo da chave `['toolkit', …]`: um gesto que deu certo invalida a
 * árvore inteira de uma vez. Aprovar uma versão muda a matriz da rotina, a
 * lista, as propostas pendentes e o contador do menu — invalidar uma chave por
 * vez seria apostar que ninguém esquece uma delas.
 *
 * As propostas chegam da mestre a qualquer hora, e não há evento de socket
 * para elas: a sondagem a cada 30 s é o que faz uma proposta nova aparecer
 * sem recarregar. O cache morre com a sessão de login (`estado/cache.ts`).
 */

const CHAVE = 'toolkit';
const INTERVALO_MS = 30_000;

export function usarRotinas(arquivadas: boolean): UseQueryResult<RotinaResumo[]> {
  return useQuery({
    queryKey: [CHAVE, 'rotinas', arquivadas],
    queryFn: () => chamar<RotinaResumo[]>(`${ROTAS.toolkit.rotinas}${arquivadas ? '?arquivadas=true' : ''}`),
    refetchInterval: INTERVALO_MS,
  });
}

export function usarRotina(id: string): UseQueryResult<RotinaDetalhe> {
  return useQuery({
    queryKey: [CHAVE, 'rotina', id],
    queryFn: () => chamar<RotinaDetalhe>(CAMINHOS.rotina(id)),
    refetchInterval: INTERVALO_MS,
  });
}

export function usarPropostas(): UseQueryResult<PropostaPendente[]> {
  return useQuery({
    queryKey: [CHAVE, 'propostas'],
    queryFn: () => chamar<PropostaPendente[]>(ROTAS.toolkit.propostas),
    refetchInterval: INTERVALO_MS,
  });
}

export function usarAlvos(): UseQueryResult<AlvoDoToolkit[]> {
  return useQuery({
    queryKey: [CHAVE, 'alvos'],
    queryFn: () => chamar<AlvoDoToolkit[]>(ROTAS.toolkit.alvos),
  });
}

export function usarVerbetes(arquivados: boolean): UseQueryResult<VerbeteResumo[]> {
  return useQuery({
    queryKey: [CHAVE, 'verbetes', arquivados],
    queryFn: () => chamar<VerbeteResumo[]>(`${ROTAS.toolkit.verbetes}${arquivados ? '?arquivados=true' : ''}`),
    refetchInterval: INTERVALO_MS,
  });
}

/**
 * Os projetos da frota, para escolher onde uma rotina nova vale. A rota é só
 * de ADMIN (ela revela pastas de toda a frota, ADR-0020): quem não é nem
 * pergunta, e a rotina dele nasce da frota.
 */
export function usarProjetos(habilitado: boolean): UseQueryResult<Projeto[]> {
  return useQuery({
    queryKey: [CHAVE, 'projetos'],
    queryFn: () => chamar<Projeto[]>(ROTAS.projetos),
    enabled: habilitado,
    staleTime: 5 * 60_000,
  });
}

/** Os gestos: cada um é uma chamada, e o sucesso invalida a árvore do toolkit. */
export const GESTOS = {
  decidir: (dados: { id: string; corpo: DecisaoToolkit }) =>
    chamar<{ ok: true }>(CAMINHOS.decisaoDaProposta(dados.id), { method: 'POST', corpo: dados.corpo }),
  marcar: (dados: { rotinaId: string; corpo: Marcacao }) =>
    chamar<RotinaDetalhe>(CAMINHOS.marcacoesDaRotina(dados.rotinaId), { method: 'POST', corpo: dados.corpo }),
  estender: (dados: { rotinaId: string; corpo: NovaExtensao }) =>
    chamar<RotinaDetalhe>(CAMINHOS.extensoesDaRotina(dados.rotinaId), { method: 'POST', corpo: dados.corpo }),
  arquivarRotina: (dados: { rotinaId: string; arquivada: boolean }) =>
    chamar<RotinaDetalhe>(CAMINHOS.rotina(dados.rotinaId), { method: 'PATCH', corpo: { arquivada: dados.arquivada } }),
  criarRotina: (corpo: CriarRotina) => chamar<RotinaDetalhe>(ROTAS.toolkit.rotinas, { method: 'POST', corpo }),
  novaVersao: (dados: { rotinaId: string; corpo: NovaVersao }) =>
    chamar<RotinaDetalhe>(CAMINHOS.versoesDaRotina(dados.rotinaId), { method: 'POST', corpo: dados.corpo }),
  criarAlvo: (corpo: CriarAlvo) => chamar<AlvoDoToolkit>(ROTAS.toolkit.alvos, { method: 'POST', corpo }),
  ativarAlvo: (dados: { id: string; ativo: boolean }) =>
    chamar<AlvoDoToolkit>(CAMINHOS.alvo(dados.id), { method: 'PATCH', corpo: { ativo: dados.ativo } }),
  criarVerbete: (corpo: CriarVerbete) => chamar<VerbeteResumo>(ROTAS.toolkit.verbetes, { method: 'POST', corpo }),
  novaVersaoVerbete: (dados: { id: string; corpo: NovaVersaoVerbete }) =>
    chamar<VerbeteResumo>(CAMINHOS.versoesDoVerbete(dados.id), { method: 'POST', corpo: dados.corpo }),
  arquivarVerbete: (dados: { id: string; arquivado: boolean }) =>
    chamar<VerbeteResumo>(CAMINHOS.verbete(dados.id), { method: 'PATCH', corpo: { arquivada: dados.arquivado } }),
} as const;

/** Um gesto do toolkit como mutação: o sucesso invalida tudo o que o toolkit mostra. */
export function usarGesto<TDados, TResposta>(fn: (dados: TDados) => Promise<TResposta>) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => cliente.invalidateQueries({ queryKey: [CHAVE] }),
  });
}
